// decimal_value is a numeric(38, 10) column, so a value outside those bounds
// is rejected by the API. The editor checks the same bounds itself, to say so
// on the field rather than after a round trip.
const MAX_INTEGER_DIGITS = 28;
const MAX_DECIMAL_PLACES = 10;

export type NumberValueError = 'numberTooLarge' | 'numberTooPrecise';

export const getNumberValueError = (
  value: number | undefined,
  { isDecimal = false }: { isDecimal?: boolean } = {}
): NumberValueError | undefined => {
  if (value === undefined || Number.isNaN(value)) {
    return undefined;
  }

  if (!Number.isFinite(value)) {
    return 'numberTooLarge';
  }

  // toFixed() would round a long value into the allowed range, and an
  // exponential toString() has no digits to count, so both go through a plain
  // decimal string.
  const [integerPart, decimalPart = ''] = Math.abs(value)
    .toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 })
    .split('.');

  if (integerPart.length > MAX_INTEGER_DIGITS) {
    return 'numberTooLarge';
  }

  if (isDecimal && decimalPart.length > MAX_DECIMAL_PLACES) {
    return 'numberTooPrecise';
  }

  return undefined;
};

// A decimal attribute can arrive as a string - DRF renders Decimal that way -
// so the editor parses whatever the table holds before it edits it.
export const toEditableNumber = (value: number | string | undefined): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};
